import { NAVIGATION_ROUTES } from "../../Common/appConfig/appConfig";
import "./ConfigurableHomeSection.css";

function toText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function getParagraphs(props) {
  if (Array.isArray(props?.paragraphs)) {
    return props.paragraphs.map(toText).filter(Boolean).slice(0, 12);
  }

  const body = toText(props?.body || props?.content);
  return body ? body.split(/\r?\n+/).map(toText).filter(Boolean).slice(0, 12) : [];
}

function getSafeImageUrl(value) {
  const source = toText(value);
  if (!source) return "";

  try {
    const url = new URL(source, window.location.origin);
    if (!["http:", "https:"].includes(url.protocol)) return "";
    return source.startsWith("/") ? `${url.pathname}${url.search}${url.hash}` : url.toString();
  } catch {
    return "";
  }
}

function getSafeTarget(item) {
  const actionCode = toText(item?.actionCode || item?.action_code);
  if (NAVIGATION_ROUTES[actionCode]) return NAVIGATION_ROUTES[actionCode];

  const route = toText(item?.route || item?.path);
  if (!route.startsWith("/") || route.startsWith("//")) return "";
  return route;
}

function ConfigAction({ item, onNavigate, className = "" }) {
  const target = getSafeTarget(item);
  const label = toText(item?.actionLabel || item?.action_label || item?.label);
  if (!target || !label) return null;

  return (
    <button className={className} type="button" onClick={() => onNavigate(target)}>
      {label}
    </button>
  );
}

function RichTextSection({ section }) {
  const props = section.props || {};
  const paragraphs = getParagraphs(props);

  return (
    <section className="app-config-home-section app-config-home-rich-text" data-app-section={section.key}>
      {props.eyebrow ? <span className="app-config-home-section__eyebrow">{toText(props.eyebrow)}</span> : null}
      {props.title ? <h2>{toText(props.title)}</h2> : null}
      {props.description ? <p className="app-config-home-section__description">{toText(props.description)}</p> : null}
      {paragraphs.map((paragraph, index) => <p key={`${section.key}-${index}`}>{paragraph}</p>)}
    </section>
  );
}

function BannerSection({ section, onNavigate }) {
  const props = section.props || {};
  const imageUrl = getSafeImageUrl(props.imageUrl || props.image_url);

  return (
    <section
      className={`app-config-home-section app-config-home-banner is-${toText(section.variant || "default")}`}
      data-app-section={section.key}
    >
      {imageUrl ? <img src={imageUrl} alt={toText(props.imageAlt || props.image_alt)} /> : null}
      <div className="app-config-home-banner__copy">
        {props.eyebrow ? <span className="app-config-home-section__eyebrow">{toText(props.eyebrow)}</span> : null}
        {props.title ? <h2>{toText(props.title)}</h2> : null}
        {props.description || props.body ? <p>{toText(props.description || props.body)}</p> : null}
        <ConfigAction item={props} onNavigate={onNavigate} className="app-config-home-section__action" />
      </div>
    </section>
  );
}

function ContentListSection({ section, onNavigate }) {
  const props = section.props || {};
  const items = Array.isArray(props.items) ? props.items.slice(0, 12) : [];

  return (
    <section className="app-config-home-section app-config-home-content-list" data-app-section={section.key}>
      {props.title ? <h2>{toText(props.title)}</h2> : null}
      {props.description ? <p className="app-config-home-section__description">{toText(props.description)}</p> : null}
      <div className="app-config-home-content-list__items">
        {items.map((item, index) => {
          const imageUrl = getSafeImageUrl(item?.imageUrl || item?.image_url);
          const target = getSafeTarget(item);
          const content = (
            <>
              {imageUrl ? <img src={imageUrl} alt={toText(item?.imageAlt || item?.image_alt)} /> : null}
              <span>
                {item?.eyebrow ? <em>{toText(item.eyebrow)}</em> : null}
                <strong>{toText(item?.title) || `콘텐츠 ${index + 1}`}</strong>
                {item?.description || item?.body ? <small>{toText(item.description || item.body)}</small> : null}
              </span>
            </>
          );

          return target ? (
            <button type="button" key={`${section.key}-${index}`} onClick={() => onNavigate(target)}>
              {content}
            </button>
          ) : (
            <article key={`${section.key}-${index}`}>{content}</article>
          );
        })}
      </div>
      <ConfigAction item={props} onNavigate={onNavigate} className="app-config-home-section__action" />
    </section>
  );
}

export default function ConfigurableHomeSection({ section, onNavigate }) {
  if (!section?.enabled) return null;
  if (section.type === "rich_text") return <RichTextSection section={section} />;
  if (section.type === "banner") return <BannerSection section={section} onNavigate={onNavigate} />;
  if (section.type === "content_list") return <ContentListSection section={section} onNavigate={onNavigate} />;
  return null;
}

export { getSafeImageUrl, getSafeTarget };

